import { useCallback, useRef } from 'react';
import { type To, useHistory, useSearchParams } from '@direct-frontend/modules-router';
import { isFunction } from '@direct-frontend/stdlib';

type NavigationOptions = {
    replace?: boolean;
    // eslint-disable-next-line -- тут реально что угодно)
    state?: any;
};

/**
 * Метод обновления searchParams
 * В аргументе новый URLSearchParams с текущими значениями
 * - null - ничего не делать с searchParams
 * - [URLSearchParams] - обновить searchParams
 * - [URLSearchParams, NavigationOptions] - обновить searchParams и дополнительно передав новое состояние записи в истории браузера
 */
export type SetActionSearchParams = (
    newSearchParams: URLSearchParams
) => null | [URLSearchParams] | [URLSearchParams, NavigationOptions];

export type SetSearchParams = (
    newSearchParams: URLSearchParams | SetActionSearchParams,
    navigateOptions?: NavigationOptions
) => void;

/**
 * Расширенный хук для работы с searchParams
 */
export function useExtendSearchParams() {
    const history = useHistory();
    const [searchParams] = useSearchParams();

    const refGetLocation = useRef(() => history.location);
    const refGetSearchParams = useRef(() => new URLSearchParams(history.location.search));
    const refGetNavigate = useRef((location: To, options?: NavigationOptions) => {
        if (options?.replace) {
            return history.replace(location, options?.state);
        }

        return history.push(location, options?.state);
    });

    const setSearchParams = useCallback<SetSearchParams>((newSearchParams, navigateOptions) => {
        const result = isFunction(newSearchParams)
            ? newSearchParams(refGetSearchParams.current())
            : ([newSearchParams, navigateOptions] satisfies [URLSearchParams, NavigationOptions | undefined]);

        if (!result) {
            return;
        }

        const [value, options] = result;

        refGetNavigate.current(
            {
                ...refGetLocation.current(),
                search: value.toString(),
            },
            options
        );
    }, []);

    const getSearchParams = useCallback(() => refGetSearchParams.current(), []);

    return { searchParams, setSearchParams, getSearchParams };
}
